import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface StudentImportSession {
  id: string;
  filename: string;
  fileType: string;
  status:
    | 'uploaded'
    | 'validated'
    | 'approved'
    | 'queued'
    | 'processing'
    | 'importing'
    | 'completed'
    | 'completed_with_errors'
    | 'failed';
  templateVersion: string;
  candidateVersion: number;
  totalRows: number;
  readyCount: number;
  warningCount: number;
  blockedCount: number;
  excludedCount: number;
  createdCount?: number;
  skippedCount?: number;
  failedCount?: number;
  approvedBy?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  approvalVersion?: number | null;
  approvedSummary?: any;
  executedAt?: string | null;
  completedAt?: string | null;
  executionJobId?: number | null;
  created: string;
  modified: string;
}

export interface CandidateNormalizedData {
  firstName?: string;
  lastName?: string;
  otherNames?: string;
  gender?: string;
  dob?: string | null;
  className?: string;
  classId?: number | string | null;
  admissionDate?: string | null;
  guardianFirstName?: string;
  guardianLastName?: string;
  guardianPhone?: string;
  guardianRelationship?: string;
  guardianEmail?: string;
  address?: string;
}

export interface CandidateRow {
  id: string;
  rowIndex: number;
  validationStatus: 'ready' | 'warning' | 'blocked';
  validationReasons: string[];
  rawData: Record<string, string>;
  normalizedData: CandidateNormalizedData;
  targetClassId?: string | number | null;
  targetClassName?: string | null;
  isExcluded?: boolean;
  exclusionReason?: string | null;
  duplicateDecision?: string | null;
  duplicateDecisionReason?: string | null;
  executionStatus?: 'pending' | 'created' | 'skipped' | 'failed' | null;
  importResult?: string | null;
  executionError?: string | null;
  importedStudentId?: number | string | null;
  created: string;
  modified?: string;
}

export interface CandidateRowsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CandidateRow[];
}

export interface ClassBreakdownItem {
  classId: string | number | null;
  className: string;
  candidateCount: number;
  hasActiveFeeStructure: boolean;
  feeItemsCount: number;
}

export interface ImportConsequencePreview {
  sessionId: string;
  candidateVersion: number;
  status: string;
  isApproved: boolean;
  approvalVersion?: number | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  totalRows: number;
  activeCandidatesCount: number;
  excludedCount: number;
  readyCount: number;
  warningCount: number;
  blockedCount: number;
  estimatedGuardiansCount: number;
  canApprove: boolean;
  cannotApproveReasons: string[];
  classBreakdown: ClassBreakdownItem[];
  billingConsequences: {
    hasActiveBillingImpact: boolean;
    activeBillsCount: number;
    notes: string[];
  };
}

export interface ImportAuditItem {
  id: string;
  rowIndex?: number | null;
  actorName: string;
  actorType: 'school' | 'ops' | string;
  action: string;
  priorValues: Record<string, any>;
  newValues: Record<string, any>;
  summary: string;
  created: string;
}

export interface ImportAuditsResponse {
  data: ImportAuditItem[];
}

export interface ImportProgressResponse {
  sessionId: string;
  status: StudentImportSession['status'];
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  executedAt?: string | null;
  completedAt?: string | null;
  jobId?: number | null;
  approvalVersion?: number | null;
}

export const studentImportApi = createApi({
  reducerPath: 'studentImportApi',
  baseQuery,
  tagTypes: ['StudentImportSession', 'CandidateRow'],
  endpoints: builder => ({
    getStudentImportSession: builder.query<StudentImportSession, string>({
      query: sessionId => `/onboarding/student-imports/${sessionId}`,
      providesTags: ['StudentImportSession'],
    }),

    getCandidateRows: builder.query<
      CandidateRowsResponse,
      {
        sessionId: string;
        status?: string;
        search?: string;
        page?: number;
        pageSize?: number;
      }
    >({
      query: ({ sessionId, status, search, page, pageSize }) => {
        const params = new URLSearchParams();
        if (status && status !== 'all') params.append('status', status);
        if (search) params.append('search', search);
        if (page) params.append('page', String(page));
        if (pageSize) params.append('pageSize', String(pageSize));

        const queryStr = params.toString() ? `?${params.toString()}` : '';
        return `/onboarding/student-imports/${sessionId}/rows${queryStr}`;
      },
      providesTags: ['CandidateRow'],
    }),

    uploadStudentRoster: builder.mutation<StudentImportSession, FormData>({
      query: formData => ({
        url: '/onboarding/student-imports/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['StudentImportSession', 'CandidateRow'],
    }),

    updateCandidateRow: builder.mutation<
      { row: CandidateRow; session: StudentImportSession },
      { sessionId: string; rowId: string; data: Record<string, any> }
    >({
      query: ({ sessionId, rowId, data }) => ({
        url: `/onboarding/student-imports/${sessionId}/rows/${rowId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['CandidateRow', 'StudentImportSession'],
    }),

    getImportConsequences: builder.query<ImportConsequencePreview, string>({
      query: sessionId => `/onboarding/student-imports/${sessionId}/consequences`,
      providesTags: ['StudentImportSession', 'CandidateRow'],
    }),

    approveImportSession: builder.mutation<
      StudentImportSession,
      { sessionId: string; candidateVersion: number }
    >({
      query: ({ sessionId, candidateVersion }) => ({
        url: `/onboarding/student-imports/${sessionId}/approve`,
        method: 'POST',
        body: { candidateVersion },
      }),
      invalidatesTags: ['StudentImportSession', 'CandidateRow'],
    }),

    getImportAudits: builder.query<ImportAuditsResponse, string>({
      query: sessionId => `/onboarding/student-imports/${sessionId}/audits`,
      providesTags: ['StudentImportSession'],
    }),

    executeImportSession: builder.mutation<
      { jobId: number; sessionId: string; status: string; isDuplicate: boolean },
      string
    >({
      query: sessionId => ({
        url: `/onboarding/student-imports/${sessionId}/execute`,
        method: 'POST',
      }),
      invalidatesTags: ['StudentImportSession', 'CandidateRow'],
    }),

    getImportProgress: builder.query<ImportProgressResponse, string>({
      query: sessionId => `/onboarding/student-imports/${sessionId}/progress`,
      providesTags: ['StudentImportSession'],
    }),
  }),
});

export const {
  useGetStudentImportSessionQuery,
  useGetCandidateRowsQuery,
  useUploadStudentRosterMutation,
  useUpdateCandidateRowMutation,
  useGetImportConsequencesQuery,
  useApproveImportSessionMutation,
  useGetImportAuditsQuery,
  useExecuteImportSessionMutation,
  useGetImportProgressQuery,
} = studentImportApi;
